import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import {
  Feature,
  getFeatureFromPlan,
  getIntervalPathParam,
  getQuotaPathParam,
  Order,
} from '../../../models';
import { useNavigate, useQuery } from '../../../routes';
import { plan } from '../../../layout/routes';
import { SUPPORTED_FEATURES } from '../../../utils/Constants';

const useStyles = makeStyles(({
  band: {
    margin: '0 30px 24px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px 16px',
  },
  button: {
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    height: 40,
    padding: '0 18px',
    border: '1.5px solid #E1E7E4',
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
    color: '#16141D',
    fontSize: 13.5,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#F6F8F7',
    },
  },
  cartIcon: {
    display: 'inline-flex',
    color: '#0F8461',
    fontSize: 17,
  },
  hint: {
    fontSize: 13,
    color: '#5B6661',
  },
}));

// Quota + cycle query params for every order item the plan page can pre-select,
// summed per feature (an order can hold two items of the same SKU), plus whether anything was
// left behind. An item is dropped when its SKU is no longer sold — R6 (Decision 16) retired True
// Automation / True Platform, and the plan page has no row to pre-select for them.
interface RepurchasePlan {
  params: Record<string, string>;
  hasDroppedItem: boolean;
}

const getRepurchasePlan = (order: Order): RepurchasePlan => {
  const quotaByFeature = new Map<Feature, { quota: number, interval: string }>();
  let hasDroppedItem = false;

  order.orderItems.forEach(item => {
    // BUNDLE_COMPONENT rows are the package's contents, not products: repurchasing them
    // individually would push 3 TestOps + 1 TestCloud... into the cart instead of the bundle.
    // The parent row re-adds the bundle SKU, whose add-ons come from the CURRENT LD config.
    if (item.type === 'TERMINATED_PRODUCT' || item.type === 'BUNDLE_COMPONENT' || item.quantity <= 0) {
      return;
    }
    const feature = getFeatureFromPlan(item.planId);
    if (!feature || !SUPPORTED_FEATURES.includes(feature)) {
      hasDroppedItem = true;
      return;
    }
    const existing = quotaByFeature.get(feature);
    quotaByFeature.set(feature, {
      quota: (existing?.quota ?? 0) + item.quantity,
      interval: item.billingCycle,
    });
  });

  const params: Record<string, string> = {};
  quotaByFeature.forEach(({ quota, interval }, feature) => {
    params[getQuotaPathParam(feature)] = String(quota);
    params[getIntervalPathParam(feature)] = interval;
  });
  return { params, hasDroppedItem };
};

// "Repurchase these items" band for canceled orders (Billing 2.0 design):
// navigates to the plan page with the order's plans, cycles and quantities
// pre-selected through the quota/cycle query params the plan page already reads.
export const RepurchaseItems = ({ order }: { order: Order }) => {
  const classes = useStyles();
  const { queryDictionary } = useQuery();
  const { navigate, replaceQuery } = useNavigate();

  const { params: repurchaseParams, hasDroppedItem } = getRepurchasePlan(order);
  if (Object.keys(repurchaseParams).length === 0) {
    return null;
  }

  const repurchase = () => {
    navigate(plan.path, replaceQuery({
      ...queryDictionary(),
      ...repurchaseParams,
    }));
  };

  return (
    <div id={`order-${order.id}-repurchase`} className={classes.band}>
      <button
        id={`order-${order.id}-repurchase-btn`}
        type="button"
        className={classes.button}
        onClick={repurchase}
      >
        <span className={classes.cartIcon}>
          <ShoppingCartOutlinedIcon fontSize="inherit" />
        </span>
        <FormattedMessage id="page.order.history.repurchase.btn" />
      </button>
      <span className={classes.hint}>
        <FormattedMessage id="page.order.history.repurchase.hint" />
      </span>
      {hasDroppedItem && (
        <span id={`order-${order.id}-repurchase-dropped`} className={classes.hint}>
          <FormattedMessage id="page.order.history.repurchase.unavailable" />
        </span>
      )}
    </div>
  );
};
